// Fila de julgamento: tamanho, espera e as submissões que falharam por
// problema de infraestrutura — as que o admin precisa rejulgar.

import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CircleCheck,
  Hourglass,
  Loader,
  ServerCrash,
  Timer,
  RefreshCw,
} from "lucide-react";
import type { QueueStatusDto } from "@compila/shared";
import { api } from "../../lib/api.js";
import { formatContestTime } from "../../lib/format.js";
import { Button } from "../../components/motion/button/base";
import { AlertMessage } from "../../components/alerts";
import { EmptyState, Loading, PageHeader } from "../../components/domain.js";
import { Identicon } from "../../components/Identicon.js";

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Não foi possível concluir a operação.";

export function AdminQueuePage(): React.JSX.Element {
  const queryClient = useQueryClient();

  const queue = useQuery({
    queryKey: ["queue"],
    queryFn: () => api.get<QueueStatusDto>("/admin/queue"),
    refetchInterval: 5_000,
  });

  const rejudge = useMutation({
    mutationFn: (id: string) => api.post(`/admin/submissions/${id}/rejudge`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["queue"] }),
  });

  if (queue.isPending) return <Loading label="Carregando a fila…" />;
  if (queue.error) {
    return (
      <div className="space-y-4">
        <AlertMessage tone="error">{errorMessage(queue.error)}</AlertMessage>
        <Button variant="secondary" onClick={() => void queue.refetch()}>
          Tentar novamente
        </Button>
      </div>
    );
  }

  const q = queue.data;
  const pending = q.submissions.QUEUED + q.submissions.JUDGING;
  const failures = q.submissions.INTERNAL_ERROR;
  // Acima de um minuto de espera, a prova deixa de dar retorno rápido à
  // equipe; a meta da especificação é p95 abaixo de 30s.
  const slow = (q.oldestPendingSeconds ?? 0) > 60;

  return (
    <div className="space-y-7">
      <PageHeader
        title="Fila de julgamento"
        description="Acompanhe o que está sendo julgado e recupere submissões que falharam por problema de infraestrutura."
      />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={Hourglass} label="Na fila" value={q.submissions.QUEUED} />
        <Stat icon={Loader} label="Julgando" value={q.submissions.JUDGING} accent />
        <Stat icon={CircleCheck} label="Concluídas" value={q.submissions.DONE} />
        <Stat
          icon={ServerCrash}
          label="Falhas do juiz"
          value={failures}
          alert={failures > 0}
        />
      </section>

      <section className="rounded-2xl border border-border bg-card p-6">
        <h2 className="flex items-center gap-2 text-sm font-medium">
          <Timer size={16} className="text-muted-foreground" aria-hidden />
          Espera
        </h2>

        {pending === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Fila vazia. Nenhuma submissão aguardando julgamento.
          </p>
        ) : (
          <p className="mt-3 text-sm">
            {pending} {pending === 1 ? "submissão pendente" : "submissões pendentes"}
            {q.oldestPendingSeconds !== null && (
              <>
                {" · a mais antiga espera há "}
                <strong className={slow ? "text-destructive" : undefined}>
                  {formatContestTime(q.oldestPendingSeconds)}
                </strong>
              </>
            )}
          </p>
        )}

        {q.queue && (
          <p className="mt-2 text-xs text-muted-foreground">
            pg-boss: {q.queue.queued} na fila, {q.queue.active} ativos, {q.queue.failed}{" "}
            falhos
          </p>
        )}
      </section>

      <section className="overflow-hidden rounded-2xl border border-border bg-card">
        <header className="flex items-center gap-2 border-b border-border px-5 py-4">
          <ServerCrash size={16} className="text-muted-foreground" aria-hidden />
          <h2 className="text-sm font-medium">Falhas do sistema de julgamento</h2>
        </header>

        <div className="p-5">
          {q.internalErrors.length === 0 ? (
            <EmptyState
              icon={CircleCheck}
              title="Nenhuma falha"
              description="Todas as submissões foram julgadas pelo Judge0 sem erro de infraestrutura."
            />
          ) : (
            <div className="space-y-4">
              <AlertMessage tone="warning">
                Estas submissões não foram julgadas por falha de infraestrutura. Elas não
                penalizam as equipes, mas precisam ser rejulgadas para contar no placar.
              </AlertMessage>

              {rejudge.error && (
                <AlertMessage tone="error">{errorMessage(rejudge.error)}</AlertMessage>
              )}

              <ul className="space-y-2">
                {q.internalErrors.map((s) => (
                  <li
                    key={s.id}
                    className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-background px-4 py-3 text-sm"
                  >
                    <Identicon seed={s.teamId} className="size-7" />
                    <span className="min-w-0 truncate font-medium">{s.teamName}</span>
                    <span className="rounded-md border border-border px-1.5 py-0.5 text-xs font-medium">
                      {s.problemLabel}
                    </span>
                    <span className="text-xs text-muted-foreground">{s.language}</span>
                    <span className="text-xs text-muted-foreground">
                      {s.judgeAttempts}{" "}
                      {s.judgeAttempts === 1 ? "tentativa" : "tentativas"}
                    </span>

                    <div className="ml-auto flex items-center gap-2">
                      <Link
                        to={`/admin/submissoes/${s.id}`}
                        className="text-xs text-accent hover:underline"
                      >
                        abrir
                      </Link>
                      <Button
                        size="sm"
                        className="rounded-full"
                        disabled={rejudge.isPending}
                        onClick={() => rejudge.mutate(s.id)}
                      >
                        <RefreshCw size={13} aria-hidden />
                        Rejulgar
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Stat({
  icon: Icone,
  label,
  value,
  accent = false,
  alert = false,
}: {
  icon: typeof Timer;
  label: string;
  value: number;
  accent?: boolean;
  alert?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border p-5 ${
        alert ? "border-destructive/40 bg-destructive/5" : "border-border bg-card"
      }`}
    >
      <span className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icone
          size={14}
          className={accent && value > 0 ? "animate-spin text-accent" : ""}
          aria-hidden
        />
        {label}
      </span>
      <p
        className={`mt-2 text-3xl font-semibold tabular-nums ${
          alert ? "text-destructive" : accent && value > 0 ? "text-accent" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}
